import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { addDays, addMinutes, format, isSameDay, setHours, startOfDay } from "date-fns";
import Cal, { getCalApi } from "@calcom/embed-react";
import { CalendarClock, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { EmptyState, Loading, ProblemAlert } from "@/components/site/Feedback";
import { api, errorMessage } from "@/lib/api";
import { timeRange, dateTime, minutesLabel } from "@/lib/format";
import { AppointmentStatus, appointmentStatusLabel, type AppointmentBookingResponse, type AppointmentDto } from "@/lib/types";
import { cn } from "@/lib/utils";

/** Cal.com embed namespace (one booker on the page). */
const calTheme = () => (document.documentElement.classList.contains("dark") ? "dark" : "light");

const CAL_NAMESPACE = "mentorship";

export function invalidateMentorship(queryClient: ReturnType<typeof useQueryClient>) {
  void queryClient.invalidateQueries({ queryKey: ["mentorship", "appointments"] });
  void queryClient.invalidateQueries({ queryKey: ["billing"] });
}

/** Session lengths and booking mode ("calcom" or "builtin"). */
export function useMentorshipOptions() {
  return useQuery({ queryKey: ["mentorship", "options"], queryFn: api.mentorship.options, staleTime: 5 * 60_000 });
}

type Booking = { response: AppointmentBookingResponse; isNew: boolean };

/**
 * Booking flow shared by "Book a session" and "Reschedule":
 * mode "calcom" → the Cal.com event of the session length, embedded in a dialog (the appointment id travels as booking
 * metadata; the API is told about the booking at once and Cal.com's webhook confirms it too);
 * mode "builtin" → the portal's own POC picker (Cal.com not configured).
 */
export function useBookingFlow() {
  const queryClient = useQueryClient();
  const [current, setCurrent] = useState<Booking | null>(null);

  const book = useMutation({
    mutationFn: ({ topic, minutes }: { topic: string | null; minutes: number }) => api.mentorship.book(topic, minutes),
    onSuccess: (r) => setCurrent({ response: r, isNew: true }),
    onError: (e) => toast.error("The session could not be booked", { description: errorMessage(e) }),
  });

  const reschedule = useMutation({
    mutationFn: (id: string) => api.mentorship.reschedule(id),
    onSuccess: (r) => setCurrent({ response: r, isNew: false }),
    onError: (e) => toast.error("Rescheduling could not be started", { description: errorMessage(e) }),
  });

  const close = (booked: boolean) => {
    // A new booking that was abandoned is dropped (no credits were taken yet).
    const abandoned = !booked && current?.isNew ? current.response.appointmentId : null;
    setCurrent(null);
    if (abandoned) {
      void api.mentorship
        .discard(abandoned)
        .catch(() => undefined)
        .finally(() => invalidateMentorship(queryClient));
    } else {
      invalidateMentorship(queryClient);
    }
  };

  const dialog =
    current?.response.mode === "calcom" ? (
      <CalBookingDialog booking={current} onClose={close} />
    ) : (
      <BuiltinSchedulerDialog booking={current} onClose={close} />
    );

  return { book, reschedule, dialog, busy: book.isPending || reschedule.isPending };
}

/** The Cal.com booker of the chosen session length (or the member's booking when rescheduling). */
function CalBookingDialog({ booking, onClose }: { booking: Booking; onClose: (booked: boolean) => void }) {
  const queryClient = useQueryClient();
  const r = booking.response;
  const [done, setDone] = useState(false);

  // Tell the API about the booking right away; Cal.com's webhook can take a minute.
  const sync = useMutation({
    mutationFn: (uid: string) => api.mentorship.syncCalBooking(r.appointmentId, uid),
    onSuccess: (a) => {
      if (a.status === AppointmentStatus.Pending) {
        toast.error("The booking was not accepted", { description: "Please check your mentorship time and try again." });
      } else {
        toast.success(booking.isNew ? "Your session is booked" : "Your session was moved", { description: timeRange(a.startTime, a.endTime) });
      }
      invalidateMentorship(queryClient);
    },
    onError: () => {
      // The webhook still confirms it; refresh the list in a moment.
      toast.success("Booking received", { description: "It appears under My appointments within a minute." });
      setTimeout(() => invalidateMentorship(queryClient), 20_000);
    },
  });

  useEffect(() => {
    let active = true;
    void (async () => {
      const cal = await getCalApi({ namespace: CAL_NAMESPACE, embedJsUrl: `${r.embedOrigin}/embed/embed.js` });
      cal("ui", {
        theme: calTheme(),
        hideEventTypeDetails: false,
        layout: "month_view",
        // The portal's gold as the booker's accent colour.
        cssVarsPerTheme: { light: { "cal-brand": "#b8893b" }, dark: { "cal-brand": "#d6b25e" } },
      });
      const onBooked = (e: { detail: { data: { uid?: string } } }) => {
        if (!active) return;
        setDone(true);
        if (e.detail.data.uid) sync.mutate(e.detail.data.uid);
      };
      cal("on", { action: "bookingSuccessfulV2", callback: onBooked as never });
      cal("on", { action: "rescheduleBookingSuccessfulV2", callback: onBooked as never });
    })();
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [r.appointmentId]);

  return (
    <Dialog open onOpenChange={(open) => !open && onClose(done)}>
      <DialogContent className="flex h-[92vh] max-w-5xl flex-col gap-3 p-4 sm:p-6">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-medium">
            {booking.isNew ? `Book your ${minutesLabel(r.durationMinutes)} session` : "Pick a new time"}
          </DialogTitle>
          <DialogDescription>
            {booking.isNew
              ? `${minutesLabel(r.durationMinutes)} of mentorship time is used once the booking is confirmed.`
              : "Moving a session is free; your mentorship time stays the same."}
          </DialogDescription>
        </DialogHeader>
        <div className="min-h-0 flex-1 overflow-auto rounded-md border border-border">
          <Cal
            namespace={CAL_NAMESPACE}
            calLink={r.calLink!}
            calOrigin={r.embedOrigin ?? undefined}
            embedJsUrl={`${r.embedOrigin}/embed/embed.js`}
            style={{ width: "100%", height: "100%", overflow: "auto" }}
            config={{
              name: r.name,
              email: r.email,
              "metadata[appointmentId]": r.appointmentId,
              // Environments sharing the Cal.com account (local dev, staging) ignore each other's bookings.
              ...(r.instance ? { "metadata[instance]": r.instance } : {}),
              layout: "month_view",
              theme: calTheme(),
            }}
          />
        </div>
        <DialogFooter>
          <Button variant={done ? "gold" : "outline"} onClick={() => onClose(done)}>
            {sync.isPending && <Loader2 className="animate-spin" />}
            {done ? "Done" : "Close"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** POC scheduler while Cal.com is not configured: next 14 days, slots 09:00–17:00 local time. */
function BuiltinSchedulerDialog({ booking, onClose }: { booking: Booking | null; onClose: (confirmed: boolean) => void }) {
  const appointmentId = booking?.response.appointmentId ?? null;
  const minutes = booking?.response.durationMinutes ?? 60;
  const days = useMemo(
    () => Array.from({ length: 14 }, (_, i) => startOfDay(addDays(new Date(), i + (new Date().getHours() >= 16 ? 1 : 0)))),
    [appointmentId],
  );
  const [day, setDay] = useState<Date | null>(null);
  const [slot, setSlot] = useState<Date | null>(null);
  const activeDay = day ?? days[0]!;
  const now = new Date();
  // Half-hour starts; the session has to end by 17:00.
  const slots = Array.from({ length: 16 }, (_, i) => addMinutes(setHours(activeDay, 9), i * 30)).filter(
    (s) => s > now && addMinutes(s, minutes) <= setHours(activeDay, 17),
  );

  const confirm = useMutation({
    mutationFn: (start: Date) =>
      api.mentorship.confirm(appointmentId!, { startTime: start.toISOString(), endTime: addMinutes(start, minutes).toISOString() }),
    onSuccess: (a) => {
      toast.success("Your session is booked", { description: timeRange(a.startTime, a.endTime) });
      setDay(null);
      setSlot(null);
      onClose(true);
    },
  });

  return (
    <Dialog
      open={appointmentId !== null}
      onOpenChange={(open) => {
        if (!open) {
          setDay(null);
          setSlot(null);
          confirm.reset();
          onClose(false);
        }
      }}
    >
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-medium">Choose a time</DialogTitle>
          <DialogDescription>{minutesLabel(minutes)} session, shown in your local time.</DialogDescription>
        </DialogHeader>

        <div className="flex gap-2 overflow-x-auto pb-2">
          {days.map((d) => (
            <button
              key={d.toISOString()}
              onClick={() => {
                setDay(d);
                setSlot(null);
              }}
              className={cn(
                "flex w-16 shrink-0 flex-col items-center rounded-md border px-2 py-2 text-xs transition-colors",
                isSameDay(d, activeDay) ? "border-gold bg-gold/10" : "border-border hover:bg-accent",
              )}
            >
              <span className="eyebrow text-[0.6rem] text-muted-foreground">{format(d, "EEE")}</span>
              <span className="font-display text-xl">{format(d, "d")}</span>
              <span className="text-muted-foreground">{format(d, "MMM")}</span>
            </button>
          ))}
        </div>

        <div className="grid grid-cols-3 gap-2 sm:grid-cols-4">
          {slots.length === 0 && <p className="col-span-full text-sm text-muted-foreground">No times left on this day.</p>}
          {slots.map((s) => (
            <button
              key={s.toISOString()}
              onClick={() => setSlot(s)}
              className={cn(
                "rounded-md border px-3 py-2 text-sm transition-colors",
                slot?.getTime() === s.getTime() ? "border-gold bg-gold/15 font-medium" : "border-border hover:bg-accent",
              )}
            >
              {format(s, "HH:mm")}
            </button>
          ))}
        </div>

        <ProblemAlert error={confirm.error} />

        <DialogFooter>
          <Button variant="gold" disabled={!slot || confirm.isPending} onClick={() => slot && confirm.mutate(slot)}>
            {confirm.isPending && <Loader2 className="animate-spin" />}
            {slot ? `Book ${format(slot, "EEE d MMM, HH:mm")}` : "Pick a time"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const statusStyle: Record<AppointmentStatus, string> = {
  [AppointmentStatus.Pending]: "border-border bg-secondary text-muted-foreground",
  [AppointmentStatus.Scheduled]: "border-gold/60 bg-gold/10 text-foreground",
  [AppointmentStatus.Confirmed]: "border-transparent bg-[image:var(--gradient-gold)] text-ink",
  [AppointmentStatus.RescheduleRequested]: "border-destructive/40 bg-destructive/10 text-destructive",
  [AppointmentStatus.CancelRequested]: "border-destructive/30 bg-destructive/5 text-destructive",
  [AppointmentStatus.ChangeRequested]: "border-gold/40 bg-gold/5 text-foreground",
  [AppointmentStatus.Cancelled]: "border-border bg-muted text-muted-foreground line-through",
  [AppointmentStatus.Completed]: "border-transparent bg-primary text-primary-foreground",
};

export function AppointmentStatusBadge({ status }: { status: AppointmentStatus }) {
  return (
    <Badge variant="outline" className={cn("whitespace-nowrap font-medium", statusStyle[status])}>
      {appointmentStatusLabel[status] ?? "Unknown"}
    </Badge>
  );
}

/** States in which the member can move the session (free) or ask to cancel it. */
const active = [
  AppointmentStatus.Scheduled,
  AppointmentStatus.Confirmed,
  AppointmentStatus.RescheduleRequested,
  AppointmentStatus.ChangeRequested,
  AppointmentStatus.CancelRequested,
];

/** "My appointments" with the member actions. */
export function AppointmentsList({ flow }: { flow: ReturnType<typeof useBookingFlow> }) {
  const queryClient = useQueryClient();
  const appointments = useQuery({ queryKey: ["mentorship", "appointments"], queryFn: () => api.mentorship.appointments() });
  const [note, setNote] = useState<AppointmentDto | null>(null);

  const discard = useMutation({
    mutationFn: (id: string) => api.mentorship.discard(id),
    onSuccess: () => {
      toast.success("Pending booking discarded");
      invalidateMentorship(queryClient);
    },
    onError: (e) => toast.error("Could not discard the booking", { description: errorMessage(e) }),
  });

  if (appointments.isPending) return <Loading label="Loading your appointments…" />;
  if (appointments.isError) return <ProblemAlert error={appointments.error} />;
  const items = appointments.data.items;
  if (items.length === 0) {
    return <EmptyState title="No sessions yet">Book your first mentorship session above.</EmptyState>;
  }

  return (
    <>
      <div className="divide-y divide-border overflow-hidden rounded-lg border border-border bg-card shadow-card">
        {items.map((a) => (
          <div key={a.id} className="flex flex-col gap-4 p-5 md:flex-row md:items-start md:justify-between">
            <div className="min-w-0 space-y-1.5">
              <div className="flex flex-wrap items-center gap-3">
                <CalendarClock className="size-4 text-gold" />
                <span className="font-medium">
                  {a.startTime ? timeRange(a.startTime, a.endTime) : `${minutesLabel(a.durationMinutes)} session — not booked yet`}
                </span>
                <AppointmentStatusBadge status={a.status} />
              </div>
              {a.topic && <p className="text-sm">Topic: {a.topic}</p>}
              {a.adminNote && <p className="text-sm text-muted-foreground">Mentor: {a.adminNote}</p>}
              {a.memberNote && <p className="text-sm text-muted-foreground">Your note: {a.memberNote}</p>}
              <p className="text-xs text-muted-foreground">
                {minutesLabel(a.durationMinutes)} · requested {dateTime(a.createdAt)}
              </p>
            </div>
            <div className="flex shrink-0 flex-wrap gap-2">
              {a.status === AppointmentStatus.Pending && (
                <>
                  <Button
                    size="sm"
                    variant="gold"
                    disabled={flow.busy}
                    onClick={() => flow.book.mutate({ topic: a.topic, minutes: a.durationMinutes })}
                  >
                    Continue booking
                  </Button>
                  <Button size="sm" variant="outline" disabled={discard.isPending} onClick={() => discard.mutate(a.id)}>
                    Discard
                  </Button>
                </>
              )}
              {active.includes(a.status) && a.status !== AppointmentStatus.CancelRequested && (
                <Button
                  size="sm"
                  variant={a.status === AppointmentStatus.RescheduleRequested ? "gold" : "outlineGold"}
                  disabled={flow.busy}
                  onClick={() => flow.reschedule.mutate(a.id)}
                >
                  {a.status === AppointmentStatus.RescheduleRequested ? "Pick a new time" : "Reschedule"}
                </Button>
              )}
              {active.includes(a.status) && a.status !== AppointmentStatus.CancelRequested && (
                <Button size="sm" variant="outline" onClick={() => setNote(a)}>
                  Request cancel
                </Button>
              )}
            </div>
          </div>
        ))}
      </div>
      <CancelRequestDialog target={note} onClose={() => setNote(null)} />
    </>
  );
}

/** Cancellations go through the mentor; once accepted the minutes come back. */
function CancelRequestDialog({ target, onClose }: { target: AppointmentDto | null; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [text, setText] = useState("");
  const send = useMutation({
    mutationFn: () => api.mentorship.requestCancel(target!.id, text.trim() || null),
    onSuccess: () => {
      toast.success("Cancellation requested", { description: "Your mentor will get back to you shortly." });
      setText("");
      invalidateMentorship(queryClient);
      onClose();
    },
  });

  return (
    <Dialog
      open={target !== null}
      onOpenChange={(open) => {
        if (!open) {
          send.reset();
          onClose();
        }
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-medium">Request a cancellation</DialogTitle>
          <DialogDescription>
            {target ? timeRange(target.startTime, target.endTime) : ""}. If the cancellation is accepted, the{" "}
            {target ? minutesLabel(target.durationMinutes) : ""} go back to your mentorship time. To move the session
            instead, use Reschedule — that is free.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <Label htmlFor="appointment-note">Note (optional)</Label>
          <Textarea id="appointment-note" rows={4} maxLength={1000} value={text} onChange={(e) => setText(e.target.value)} />
        </div>
        <ProblemAlert error={send.error} />
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
          <Button variant="destructive" disabled={send.isPending} onClick={() => send.mutate()}>
            {send.isPending && <Loader2 className="animate-spin" />}
            Send request
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
